/**
 * A model's own colours and finishes, as the app offers them on a piece's colour button (`FurnitureColors` →
 * `FurnitureOptionPopup`): the variations its definition lists (`<Variations><Item Id Caption Colors>`, a texture or a
 * colour per part), each shown by the picture the catalog keeps under the variation's id. A piece wears one by its
 * `variationId`, which the renderer dresses it by (`ModelLibrary.variationSurfaces`); without one it wears the first.
 */
import { attr, kids, parseXml, type Node } from '../core/hp/xml';
import { fetchModels, type CatalogModel } from './catalogApi';
import { loadSearchData, searchDataNow } from './catalogSearch';
import { uiCaptionOf } from './catalogCard';
import { language, t, type TranslationKey } from '../i18n/I18n';

export interface ModelVariation {
  id: string;
  /** Its name, in the interface language where the app has one. */
  title: string;
  /** The catalog's picture of the model in it, when there is one. */
  thumb?: string;
  /** What the first of its parts is dressed in, for a swatch where there is no picture. */
  swatch: { texture?: string; color?: string };
}

/** The app's keys (Langs/*.xml) of the colour captions of its own models' variations, by lower-case caption. */
const COLOUR_NAMES: Record<string, TranslationKey> = {
  gray: 'Gray', grey: 'Gray', white: 'White', black: 'Black', 'black-brown': 'Black-brown', 'black brown': 'Black brown',
  'medium brown': 'Medium brown', pink: 'Pink', green: 'Green', blue: 'Blue', yellow: 'Yellow', red: 'Red',
  brown: 'Brown', natural: 'Natural', 'gray-brown': 'Gray-brown', 'gray brown': 'Gray brown',
  'high gloss white': 'High gloss white', 'white-black': '#ModelVariationsWhiteBlack', 'beech effect': 'Beech effect', 'birch effect': 'Birch effect',
  birch: 'Birch', walnut: '#ModelVariationsWalnut', beige: 'Beige', 'antique stain': 'Antique stain', 'golden brown': 'Golden brown',
  'beech veneer': 'Beech veneer', 'white stain': 'White stain', 'light brown': 'Light brown', oak: 'Oak',
  'gloss black': '#ModelVariationsGlossBlack', 'gloss red': 'Gloss red', 'gloss gray': 'Gloss gray', 'high gloss black': 'High gloss black',
  'high gloss red': 'High gloss red', 'high gloss gray': 'High gloss gray', 'black steel': 'Black steel', multicolor: 'Multicolor',
};

function titleOf(caption: string | undefined, colors: string | undefined, fallback: string): string {
  // The search data's captions are the app's Russian for them; in English a caption is its own name.
  const known = (value: string | undefined) => {
    const key = value ? COLOUR_NAMES[value.trim().toLowerCase()] : undefined;
    return key ? t(key) : value && language() === 'ru' ? searchDataNow()?.captions[value.trim()] : undefined;
  };
  return known(caption) ?? (caption?.trim() || undefined) ?? (colors?.split(',').map(known).filter(Boolean).join(', ') || undefined) ?? fallback;
}

/** The variations a definition lists, in its order; none when it lists fewer than two to choose from. */
export function readVariations(xml: string, thumbs: CatalogModel['thumbs'] = []): ModelVariation[] {
  let parsed: Node;
  try { parsed = parseXml(xml.replace(/^﻿/, '')); } catch { return []; }
  const root: Node | undefined = parsed?.Item && typeof parsed.Item === 'object' ? parsed.Item : undefined;
  const items = kids(kids(root, 'Variations')[0], 'Item');
  if (items.length < 2) return [];
  return items.map((item, index) => {
    const id = attr(item, 'Id') ?? String(index + 1);
    const part = kids(item, 'Item')[0] ?? item;
    // The first variation is the model's own: its name is the root's, which the app translates (CaptionsUI).
    const title = index === 0 && !attr(item, 'Caption') ? uiCaptionOf(attr(root, 'CaptionsUI')) ?? titleOf(attr(root, 'Caption'), attr(item, 'Colors'), t('#ModelVariationsVariant', 1))
      : titleOf(attr(item, 'Caption'), attr(item, 'Colors'), t('#ModelVariationsVariant', index + 1));
    return { id, title, thumb: thumbs?.find(thumb => thumb.name === id && thumb.url)?.url,
      swatch: { ...(attr(part, 'Texture') ? { texture: attr(part, 'Texture') } : {}), ...(attr(part, 'Color') ? { color: attr(part, 'Color') } : {}) } };
  });
}

const known = new Map<string, Promise<ModelVariation[]>>();
type Fetch = typeof fetch;
let fetchImpl: Fetch | null = null;
/** Tests hand in their own fetch; the page uses the browser's. */
export function useVariationFetch(impl: Fetch | null): void { fetchImpl = impl; known.clear(); }

/**
 * The variations of a catalog model, fetched once: its document for the pictures, its definition for the list. An
 * empty list for a model with one look, or one the catalog cannot tell about — a failure is forgotten and asked again.
 */
export function modelVariations(modelId: string): Promise<ModelVariation[]> {
  let entry = known.get(modelId);
  if (!entry) {
    entry = (async () => {
      // An app's own model is keyed by its built-in path even when a project names it by its catalog id.
      const found = await fetchModels([modelId]);
      const model = found[modelId] ?? Object.values(found).find(value => value.id === modelId || value.builtinId === modelId);
      const url = model?.definition;
      if (!model || !url || !/^https?:\/\//.test(url)) return [];
      const response = await (fetchImpl ?? fetch)(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await loadSearchData().catch(() => null);
      return readVariations(await response.text(), model.thumbs);
    })();
    const mine = entry;
    mine.catch(() => { if (known.get(modelId) === mine) known.delete(modelId); });
    known.set(modelId, entry);
  }
  return entry;
}
